import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiMapPin, FiUsers, FiHeart, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

const Section = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, white 0%, #fafafa 50%, #f4f4f5 100%);
  &#past-events {
    scroll-margin-top: 80px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  color: var(--primary-blue);
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.3rem;
  color: var(--medium-gray);
  max-width: 800px;
  margin: 0 auto 60px;
  line-height: 1.6;
`;

const EventsCarouselContainer = styled.div`
  position: relative;
  margin-bottom: 40px;
`;

const EventsCarouselWrapper = styled.div`
  overflow: hidden;
  border-radius: 16px;
`;

const EventsCarouselSlider = styled.div`
  display: flex;
  gap: 30px;
  transition: transform 0.3s ease;
  transform: translateX(-${props => props.slideOffset || 0}px);
`;

const CarouselNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  border: 2px solid var(--primary-orange);
  color: var(--primary-orange);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  z-index: 2;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    background: var(--primary-orange);
    color: white;
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    &:hover {
      transform: translateY(-50%);
      background: white;
      color: var(--primary-orange);
    }
  }

  &.prev {
    left: -25px;
  }

  &.next {
    right: -25px;
  }
`;

const SeeAllButton = styled(motion.button)`
  background: none;
  border: 2px solid var(--primary-orange);
  color: var(--primary-orange);
  padding: 12px 32px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 20px auto 0;
  display: block;

  &:hover {
    background: var(--primary-orange);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(249, 115, 22, 0.3);
  }
`;

const EventsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
`;

const EventCard = styled(motion.div)`
  background: var(--light-gray);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  width: 350px;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
`;

const EventImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, var(--primary-orange), var(--warm-gold));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  position: relative;
  overflow: hidden;
  cursor: pointer;
`;

const EventPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.5s ease;
`;

// PhotoIndicator removed - not used in current implementation

const EventBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--primary-blue);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const EventIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(45deg, #D4A574, #F4D03F);
  border-radius: 12px 6px 6px 12px;
  position: relative;
  border: 3px solid #B8860B;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '🌯';
    font-size: 30px;
  }
`;

const EventContent = styled.div`
  padding: 24px;
`;

const EventTitle = styled.h3`
  font-size: 1.4rem;
  color: var(--primary-blue);
  margin-bottom: 16px;
  font-weight: 700;
`;

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--medium-gray);
  font-size: 0.9rem;
  
  svg {
    color: var(--primary-orange);
    min-width: 16px;
  }
`;

const EventDescription = styled.p`
  color: var(--medium-gray);
  line-height: 1.6;
  margin-bottom: 16px;
  font-size: 0.95rem;
  
  &.truncated {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const ReadMoreLink = styled.button`
  background: none;
  border: none;
  color: var(--primary-orange);
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  margin-bottom: 16px;
  padding: 0;
  
  &:hover {
    color: var(--primary-blue);
  }
`;

const EventStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-orange);
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: var(--medium-gray);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Add Modal styled components
const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 40px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--medium-gray);
  
  &:hover {
    color: var(--primary-blue);
  }
`;

const ModalTitle = styled.h2`
  color: var(--primary-blue);
  margin-bottom: 24px;
  font-size: 1.8rem;
`;

// ModalPhotoGrid and ModalPhoto removed - not used in current modal implementation

const ModalPhotoGallery = styled.div`
  position: relative;
  margin: 20px 0;
`;

const ModalPhotoContainer = styled.div`
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;

  .modal-nav, .modal-indicator {
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover .modal-nav, &:hover .modal-indicator {
    opacity: 1;
  }
`;

const ModalPhotoDisplay = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.5s ease;
`;

const ModalPhotoNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid var(--primary-orange);
  color: var(--primary-orange);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  z-index: 2;

  &:hover {
    background: var(--primary-orange);
    color: white;
    transform: translateY(-50%) scale(1.1);
  }

  &.prev {
    left: 10px;
  }

  &.next {
    right: 10px;
  }
`;

const ModalPhotoIndicator = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const PastEvents = () => {
  const [pastEvents, setPastEvents] = useState([]);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [eventsCurrentSlide, setEventsCurrentSlide] = useState(0);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentPhotoIndices, setCurrentPhotoIndices] = useState({});
  const [modalPhotoIndex, setModalPhotoIndex] = useState(0);
  const carouselItemWidth = 380;

  useEffect(() => {
    const savedEvents = localStorage.getItem('ahaarsewa_events');
    if (savedEvents) {
      const events = JSON.parse(savedEvents);
      setPastEvents(events.filter(event => event.visible));
    } else {
      const defaultEvents = [
        {
          id: 1,
          title: "Holiday Burrito Drive",
          date: "December 15, 2024",
          location: "Sewa Community Center, Milpitas",
          sponsor: "TechForward Inc.",
          beneficiary: "Sacred Heart Community Service",
          volunteers: 25,
          burritos: 350,
          served: 350,
          description: "A heartwarming holiday event bringing together families and tech professionals to prepare nutritious meals for our community partners.",
          visible: true,
          photos: [
            {
              id: 1,
              name: "Sample Photo 1",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NzM4NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkV2ZW50IFBob3RvPC90ZXh0Pjwvc3ZnPg=="
            },
            {
              id: 2,
              name: "Sample Photo 2", 
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWZmNmZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OGJhNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkNvbW11bml0eSBTZXJ2aWNlPC90ZXh0Pjwvc3ZnPg=="
            }
          ]
        },
        {
          id: 2,
          title: "Diwali Celebration Sewa",
          date: "November 8, 2024",
          location: "Sewa Community Center, Milpitas",
          sponsor: "Patel Family Foundation",
          beneficiary: "Alameda County Food Bank",
          volunteers: 30,
          burritos: 400,
          served: 400,
          description: "Celebrating the festival of lights by spreading joy through service, sponsored in memory of beloved grandmother Sushila Patel.",
          visible: true,
          photos: [
            {
              id: 3,
              name: "Sample Photo 3",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmVmM2NkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2Q5N2Y2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRpd2FsaSBDZWxlYnJhdGlvbjwvdGV4dD48L3N2Zz4="
            }
          ]
        },
        {
          id: 3,
          title: "Team Building Burrito Event",
          date: "October 22, 2024",
          location: "Sewa Community Center, Milpitas",
          sponsor: "StartupSuccess Solutions",
          beneficiary: "Bill Wilson Center",
          volunteers: 20,
          burritos: 250,
          served: 250,
          description: "A corporate team building event that combined professional development with community service for a meaningful impact.",
          visible: true,
          photos: [
            {
              id: 4,
              name: "Sample Photo 4",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGNmZGY0Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzE2N2Y0NCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkNvcnBvcmF0ZSBTZXJ2aWNlPC90ZXh0Pjwvc3ZnPg=="
            },
            {
              id: 5,
              name: "Sample Photo 5",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTBmMmZlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzJkNjNkYiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPlRlYW0gQnVpbGRpbmc8L3RleHQ+PC9zdmc+"
            }
          ]
        },
        {
          id: 4,
          title: "Birthday Blessing Burritos",
          date: "September 18, 2024",
          location: "Sewa Community Center, Milpitas",
          sponsor: "The Kumar Family",
          beneficiary: "HomeFirst Services",
          volunteers: 15,
          burritos: 200,
          served: 200,
          description: "Celebrating young Arya Kumar's 10th birthday by serving others, teaching the next generation about compassion and giving.",
          visible: true,
          photos: [
            {
              id: 6,
              name: "Sample Photo 6",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmVlMmU4Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2RjMjYyNiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkJpcnRoZGF5IFNlcnZpY2U8L3RleHQ+PC9zdmc+"
            }
          ]
        }
      ];
      setPastEvents(defaultEvents.filter(event => event.visible));
      localStorage.setItem('ahaarsewa_events', JSON.stringify(defaultEvents));
    }
  }, []);

  // Auto-rotate photos for events with multiple photos
  useEffect(() => {
    const intervals = {};
    
    pastEvents.forEach(event => {
      if (event.photos && event.photos.length > 1) {
        intervals[event.id] = setInterval(() => {
          setCurrentPhotoIndices(prev => ({
            ...prev,
            [event.id]: ((prev[event.id] || 0) + 1) % event.photos.length
          }));
        }, 3000); // Change photo every 3 seconds
      }
    });

    // Cleanup intervals when component unmounts or events change
    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, [pastEvents]);

  // Auto-rotate photos in modal when it's open
  useEffect(() => {
    let modalInterval;
    
    if (selectedEvent && selectedEvent.photos && selectedEvent.photos.length > 1) {
      modalInterval = setInterval(() => {
        setModalPhotoIndex(prevIndex => (prevIndex + 1) % selectedEvent.photos.length);
      }, 4000); // Change photo every 4 seconds (slightly slower than card rotation)
    }

    return () => {
      if (modalInterval) {
        clearInterval(modalInterval);
      }
    };
  }, [selectedEvent]);

  const toggleDescription = (eventId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const nextEventsSlide = () => {
    const maxSlide = Math.max(0, pastEvents.length - 3);
    setEventsCurrentSlide(prev => Math.min(prev + 1, maxSlide));
  };

  const prevEventsSlide = () => {
    setEventsCurrentSlide(prev => Math.max(prev - 1, 0));
  };

  const renderEventCard = (event, index) => {
    const currentPhotoIndex = currentPhotoIndices[event.id] || 0;
    const currentPhoto = event.photos && event.photos.length > 0 ? event.photos[currentPhotoIndex] : null;
    
    return (
      <EventCard key={index} onClick={() => openModal(event)}>
        <EventImage>
          {currentPhoto ? (
            <EventPhoto src={currentPhoto.data} alt={currentPhoto.name} />
          ) : (
            <EventIcon />
          )}
          <EventBadge>Past Event</EventBadge>
        </EventImage>
        <EventContent>
          <EventTitle>{event.title}</EventTitle>
          <EventDetails>
            <EventDetail>
              <FiCalendar />
              {event.date}
            </EventDetail>
            <EventDetail>
              <FiMapPin />
              {event.location}
            </EventDetail>
            <EventDetail>
              <FiHeart />
              Sponsored by: {event.sponsor}
            </EventDetail>
            <EventDetail>
              <FiUsers />
              Partner: {event.beneficiary}
            </EventDetail>
          </EventDetails>
          <EventDescription className={!expandedDescriptions[event.id] ? 'truncated' : ''}>
            {event.description}
          </EventDescription>
          {event.description && event.description.length > 150 && (
            <ReadMoreLink onClick={(e) => {
              e.stopPropagation();
              toggleDescription(event.id);
            }}>
              {expandedDescriptions[event.id] ? 'Read Less' : 'Read More'}
            </ReadMoreLink>
          )}
          <EventStats>
            <StatItem>
              <StatNumber>{event.volunteers}</StatNumber>
              <StatLabel>Volunteers</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{event.burritos}</StatNumber>
              <StatLabel>Burritos Made</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{event.served}</StatNumber>
              <StatLabel>People Served</StatLabel>
            </StatItem>
          </EventStats>
        </EventContent>
      </EventCard>
    );
  };

  // Reset modal photo index when opening a new event
  const openModal = (event) => {
    setModalPhotoIndex(0);
    setSelectedEvent(event);
  };

  return (
    <Section id="past-events">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Past Events & Impact
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          See how our community comes together to make a difference. Each event represents 
          countless hours of love, service, and commitment to fighting food insecurity.
        </SectionSubtitle>

        {!showAllEvents ? (
          <>
            <EventsCarouselContainer>
              <EventsCarouselWrapper
                as={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <EventsCarouselSlider 
                  slideOffset={eventsCurrentSlide * carouselItemWidth}
                >
                  {pastEvents.map((event, index) => renderEventCard(event, index))}
                </EventsCarouselSlider>
              </EventsCarouselWrapper>
              
              {pastEvents.length > 1 && (
                <>
                  <CarouselNavButton 
                    className="prev" 
                    onClick={prevEventsSlide}
                    disabled={eventsCurrentSlide === 0}
                  >
                    <FiChevronLeft />
                  </CarouselNavButton>
                  <CarouselNavButton 
                    className="next" 
                    onClick={nextEventsSlide}
                    disabled={eventsCurrentSlide >= pastEvents.length - 3}
                  >
                    <FiChevronRight />
                  </CarouselNavButton>
                </>
              )}
            </EventsCarouselContainer>
            
            <SeeAllButton
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              onClick={() => setShowAllEvents(true)}
            >
              See All Events
            </SeeAllButton>
          </>
        ) : (
          <EventsGrid
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            {pastEvents.map((event, index) => renderEventCard(event, index))}
          </EventsGrid>
        )}

        <AnimatePresence>
          {selectedEvent && (
            <Modal
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedEvent(null)}
            >
              <ModalContent
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <ModalClose onClick={() => setSelectedEvent(null)}>
                  <FiX />
                </ModalClose>
                <ModalTitle>{selectedEvent.title}</ModalTitle>
                
                <EventDetails>
                  <EventDetail>
                    <FiCalendar />
                    {selectedEvent.date}
                  </EventDetail>
                  <EventDetail>
                    <FiMapPin />
                    {selectedEvent.location}
                  </EventDetail>
                  <EventDetail>
                    <FiHeart />
                    Sponsored by: {selectedEvent.sponsor}
                  </EventDetail>
                  <EventDetail>
                    <FiUsers />
                    Partner: {selectedEvent.beneficiary}
                  </EventDetail>
                </EventDetails>

                <EventDescription style={{ marginTop: '20px', marginBottom: '20px' }}>
                  {selectedEvent.description}
                </EventDescription>

                <EventStats style={{ marginBottom: '20px' }}>
                  <StatItem>
                    <StatNumber>{selectedEvent.volunteers}</StatNumber>
                    <StatLabel>Volunteers</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatNumber>{selectedEvent.burritos}</StatNumber>
                    <StatLabel>Burritos Made</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatNumber>{selectedEvent.served}</StatNumber>
                    <StatLabel>People Served</StatLabel>
                  </StatItem>
                </EventStats>

                {selectedEvent.photos && selectedEvent.photos.length > 0 && (
                  <>
                    <h3 style={{ color: 'var(--primary-blue)', marginBottom: '16px' }}>Event Photos</h3>
                    <ModalPhotoGallery>
                      <ModalPhotoContainer>
                        <ModalPhotoDisplay
                          src={selectedEvent.photos[modalPhotoIndex].data}
                          alt={selectedEvent.photos[modalPhotoIndex].name}
                        />
                        {selectedEvent.photos.length > 1 && (
                          <>
                            <ModalPhotoNav
                              className="prev modal-nav"
                              onClick={() => setModalPhotoIndex((prevIndex) => (prevIndex - 1 + selectedEvent.photos.length) % selectedEvent.photos.length)}
                            >
                              <FiChevronLeft />
                            </ModalPhotoNav>
                            <ModalPhotoNav
                              className="next modal-nav"
                              onClick={() => setModalPhotoIndex((prevIndex) => (prevIndex + 1) % selectedEvent.photos.length)}
                            >
                              <FiChevronRight />
                            </ModalPhotoNav>
                            <ModalPhotoIndicator className="modal-indicator">
                              {modalPhotoIndex + 1} / {selectedEvent.photos.length}
                            </ModalPhotoIndicator>
                          </>
                        )}
                      </ModalPhotoContainer>
                    </ModalPhotoGallery>
                  </>
                )}
              </ModalContent>
            </Modal>
          )}
        </AnimatePresence>
      </Container>
    </Section>
  );
};

export default PastEvents; 